import { boolean, mixed, object, string } from "yup";

import {
  CreateOrUpdateBasicPolicyDetailsInput,
  CreateOrUpdatePolicyholderInput,
  PolicyholderType,
  OrganizationType,
  OrganizationRelationshipType,
} from "@covertree/new-business-graphql";
import {
  firstName,
  middleName,
  lastName,
  dob,
  phoneNumber,
  optionalPhoneNumber,
  sequoiaEmail,
  sequoiaPhoneNumber,
  emailRequired,
  numberOfVehicles,
  occupationType,
} from "@covertree/utils";
import { ExtraQuestions } from "@covertree/new-business-redux";

export type PersonalDetailsForm = Omit<
  CreateOrUpdatePolicyholderInput,
  "mailingAddress"
> &
  Pick<CreateOrUpdateBasicPolicyDetailsInput, "insuredDateOfBirth">;

export type PersonalDetailsBasicForm = Pick<
  PersonalDetailsForm,
  "firstName" | "lastName" | "middleName" | "insuredDateOfBirth"
>;

export type ContactDetailsForm = Pick<
  PersonalDetailsForm,
  "emailAddress" | "primaryContactNumber"
>;

export type PersonalDetailsWithExtraQuestionsForm = PersonalDetailsForm &
  ExtraQuestions;

export const personalDetailsBasicFormSchema = object({
  firstName: firstName(),
  middleName: middleName(),
  lastName: lastName(),
  insuredDateOfBirth: dob(),
  occupationType: occupationType(),
  numberOfVehicles: numberOfVehicles(),
});

export const contactDetailsFormSchema = object({
  emailAddress: emailRequired(),
  primaryContactNumber: phoneNumber().required("Phone number is required"),
});

export const personalDetailsFormSchema = (isBookConversion: boolean) =>
  object({
    type: mixed<PolicyholderType>()
      .required("Primary insured type is required")
      .oneOf(Object.values(PolicyholderType), "Choose valid value"),
    firstName: firstName(),
    middleName: middleName(),
    lastName: lastName(),
    insuredDateOfBirth: dob(),
    primaryContactNumber: sequoiaPhoneNumber(isBookConversion),
    secondaryContactNumber: optionalPhoneNumber(),
    occupationType: occupationType(),
    numberOfVehicles: numberOfVehicles(),
    emailAddress: string().when("noEmail", ([noEmail]) => {
      if (noEmail) return string().optional();
      return sequoiaEmail(isBookConversion);
    }),
    orgName: string().when("type", ([primaryInsuredType]) =>
      primaryInsuredType === PolicyholderType.Organization
        ? string()
            .required("Organization name is required")
            .test(
              "not-allowed-multiple-spaces",
              "Last name cannot have leading or trailing spaces",
              (field) => !field || !/\s{2,}/.test(field)
            )
            .trim("Last name cannot have leading or trailing spaces")
            .strict(true)
        : string().notRequired()
    ),
    orgType: mixed<OrganizationType>().when("type", ([primaryInsuredType]) =>
      primaryInsuredType === PolicyholderType.Organization
        ? mixed()
            .required("Organization type is required")
            .oneOf(Object.values(OrganizationType), "Choose valid value")
        : string().notRequired()
    ),
    repRelationToOrg: mixed<OrganizationRelationshipType>().when(
      "type",
      ([primaryInsuredType]) =>
        primaryInsuredType === PolicyholderType.Organization
          ? mixed()
              .required("Relationship type is required")
              .oneOf(
                Object.values(OrganizationRelationshipType),
                "Choose valid value"
              )
          : string().notRequired()
    ),
    describeOrgType: string().when(["type", "orgType"], ([type, orgType]) => {
      return type === PolicyholderType.Organization &&
        orgType === OrganizationType.NotListed
        ? string()
            .required(
              'You must describe the organization if "Not Listed" was selected for "Organization type"'
            )
            .test(
              "not-allowed-multiple-spaces",
              "Last name cannot have leading or trailing spaces",
              (field) => !field || !/\s{2,}/.test(field)
            )
            .trim("Last name cannot have leading or trailing spaces")
            .strict(true)
        : string().notRequired();
    }),
    noEmail: boolean().optional(),
    physicallyMailDocuments: boolean().when("noEmail", ([noEmail]) =>
      noEmail === true ? boolean().required().isTrue() : boolean().optional()
    ),
    hasAutoPolicy: boolean().optional(),
    hasAnotherCoverTreePolicy: boolean().optional(),
  });
